import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";
import { Button, Input } from "../ui";
import { useCoreDoc } from "./useCoreDoc";
import { useDocState } from "../core/useDocSelector";
import { setSetting, useSettings } from "./settings";
import { useLevels } from "./levels";
import { useComfort } from "./comfort";
import { useSession } from "./session";
import { LANGUAGES, setDeviceLanguage, type Language } from "../i18n";
import { createTask, listPropDefs, applyQuickAdd } from "../core/ops";
import { parseQuickAdd } from "../core/quickadd";
import { ensureStarterDashboard } from "../dashboards/starter";
import { useDevice } from "./device";

type Preset = "default" | "calm" | "plain";

/**
 * First run (spec §9.24): one screen per step, no scrolling, every step skippable, ≤ 12 interactions
 * to a first done task and a first recurring task with a visible want. The password step is at
 * install time (install.sh) so it is not repeated here.
 */
export function WelcomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { handle } = useCoreDoc();
  const { settled } = useDocState(handle);
  const settings = useSettings(handle);
  const levels = useLevels();
  const levelNote = levels.settings;
  const comfort = useComfort();
  // a new person chooses how much Drang shows at first; the owner (an admin) stays at Expert (UI cleanup U5)
  const isAdmin = useSession((st) => st.session?.profile?.isAdmin) ?? true;
  const [start, setStart] = useState<"simple" | "power">("power");
  const setDashboard = useDevice((s) => s.setDashboard);
  const [step, setStep] = useState(0);
  const [preset, setPreset] = useState<Preset>("default");
  const [things, setThings] = useState(["", "", ""]);
  const [repeat, setRepeat] = useState("");
  const [every, setEvery] = useState(7);

  if (!handle || !settled) return <p className="p-8 text-neutral-500">{t("session.loading")}</p>;
  // already set up (on another device, or the state arrived a moment later): nothing to ask
  if (settings.onboarded && step === 0) return <Navigate to="/" replace />;
  const doc = handle.doc;

  const finish = () => {
    setSetting(doc, "onboarded", true);
    const dash = ensureStarterDashboard(doc);
    setDashboard(dash.slug);
    navigate(`/d/${dash.slug}`, { replace: true });
  };

  // the preset is the person's comfort (ADR 0032), the same on every device
  const applyPreset = () => {
    void comfort.set({
      reducedMotion: preset === "calm",
      muteBadges: preset === "calm",
      plainColors: preset === "plain",
    });
    if (!isAdmin) void levels.set("settings", start);
  };

  const steps = [
    // 0 — language, comfort preset and (for a new person) how much to show
    <div key="lang" className="flex flex-col gap-4">
      <h2 className="text-[17px] font-semibold">{t("welcome.langTitle")}</h2>
      <div className="flex gap-2" role="radiogroup" aria-label={t("language.label")}>
        {LANGUAGES.map((l) => (
          <Button
            key={l}
            variant={i18n.language === l ? "primary" : "secondary"}
            onClick={() => setDeviceLanguage(l as Language)}
            aria-pressed={i18n.language === l}
          >
            {t(`language.${l}`)}
          </Button>
        ))}
      </div>
      <h2 className="mt-2 text-[17px] font-semibold">{t("welcome.presetTitle")}</h2>
      <div className="flex flex-col gap-2" role="radiogroup" aria-label={t("welcome.presetTitle")}>
        {(["default", "calm", "plain"] as Preset[]).map((p) => (
          <label
            key={p}
            className="kss-surface flex cursor-pointer items-start gap-3 p-3 has-[:checked]:border-accent"
          >
            <input
              type="radio"
              name="preset"
              value={p}
              checked={preset === p}
              onChange={() => setPreset(p)}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-medium">{t(`welcome.preset.${p}`)}</span>
              <span className="block text-[12.5px] text-neutral-500">{t(`welcome.preset.${p}Hint`)}</span>
            </span>
          </label>
        ))}
      </div>
      {!isAdmin && (
        <>
          <h2 className="mt-2 text-[17px] font-semibold">{t("welcome.startTitle")}</h2>
          <div className="flex flex-col gap-2" role="radiogroup" aria-label={t("welcome.startTitle")}>
            {(["simple", "power"] as const).map((l) => (
              <label
                key={l}
                className="kss-surface flex cursor-pointer items-start gap-3 p-3 has-[:checked]:border-accent"
                data-testid={`welcome-start-${l}`}
              >
                <input
                  type="radio"
                  name="start"
                  value={l}
                  checked={start === l}
                  onChange={() => setStart(l)}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium">{t(`welcome.start_${l}`)}</span>
                  <span className="block text-[12.5px] text-neutral-500">{t(`welcome.start_${l}Hint`)}</span>
                </span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>,
    // 1 — three things
    <div key="things" className="flex flex-col gap-3">
      <h2 className="text-[17px] font-semibold">{t("welcome.thingsTitle")}</h2>
      <p className="text-[12.5px] text-neutral-500">{t("welcome.thingsHint")}</p>
      {things.map((v, i) => (
        <Input
          key={i}
          value={v}
          autoFocus={i === 0}
          aria-label={t("welcome.thing", { n: i + 1 })}
          placeholder={t("welcome.thing", { n: i + 1 })}
          data-testid={`welcome-thing-${i}`}
          onChange={(e) => setThings(things.map((x, j) => (j === i ? e.target.value : x)))}
        />
      ))}
    </div>,
    // 2 — anything that repeats
    <div key="repeat" className="flex flex-col gap-3">
      <h2 className="text-[17px] font-semibold">{t("welcome.repeatTitle")}</h2>
      <p className="text-[12.5px] text-neutral-500">{t("welcome.repeatHint")}</p>
      <Input
        value={repeat}
        autoFocus
        aria-label={t("welcome.repeatTitle")}
        placeholder={t("welcome.repeatPlaceholder")}
        data-testid="welcome-repeat"
        onChange={(e) => setRepeat(e.target.value)}
      />
      <label className="flex items-center gap-2 text-sm">
        {t("welcome.every")}
        <Input
          type="number"
          min={1}
          max={365}
          className="w-20"
          value={every}
          aria-label={t("welcome.everyDays")}
          data-testid="welcome-every"
          onChange={(e) => setEvery(Math.max(1, Number(e.target.value) || 1))}
        />
        {t("welcome.days")}
      </label>
    </div>,
    // 3 — done
    <div key="done" className="flex flex-col gap-3">
      <h2 className="text-[17px] font-semibold">{t("welcome.doneTitle")}</h2>
      <p className="text-sm text-neutral-500">{t("welcome.doneHint")}</p>
    </div>,
  ];

  const next = () => {
    if (step === 0) applyPreset();
    if (step === 1) {
      const defs = listPropDefs(doc);
      for (const title of things.map((x) => x.trim()).filter(Boolean))
        applyQuickAdd(doc, parseQuickAdd(title, { propDefs: defs, now: new Date() }));
    }
    if (step === 2 && repeat.trim()) {
      createTask(doc, { title: repeat.trim(), typeId: "recurring", props: { "rec.periodDays": every } });
    }
    if (step === steps.length - 1) finish();
    else setStep(step + 1);
  };

  return (
    <div
      className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-6 py-10"
      data-testid="welcome"
      data-step={step}
    >
      <div className="flex items-center gap-2.5">
        <span className="grid h-7 w-7 place-items-center rounded-[7px] bg-accent text-[13px] font-bold tracking-[0.02em] text-white">
          Dr
        </span>
        <h1 className="text-[26px] font-semibold">{t("welcome.title")}</h1>
        <span className="ml-auto font-mono text-[12px] text-neutral-500">
          {step + 1}/{steps.length}
        </span>
      </div>
      <form
        className="kss-surface p-6"
        onSubmit={(e) => {
          e.preventDefault();
          next();
        }}
      >
        {steps[step]}
        <div className="mt-6 flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={() => (step === steps.length - 1 ? finish() : setStep(step + 1))}
            data-testid="welcome-skip"
          >
            {t("welcome.skip")}
          </Button>
          <Button type="submit" data-testid="welcome-next">
            {step === steps.length - 1 ? t("welcome.start") : t("welcome.next")}
          </Button>
        </div>
      </form>
      <p className="text-center text-[12px] text-neutral-500">
        {levelNote === "simple" ? t("welcome.levelNote") : ""}
      </p>
    </div>
  );
}
